import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { formatCents } from '@pirouette/shared';
import {
  useSubscriptionPlans,
  useCreateSubscriptionPlan,
  useToggleSubscriptionPlan,
} from '@/data/stripe';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/billing/plans')({
  component: AdminPlans,
});

function AdminPlans() {
  const plans = useSubscriptionPlans();
  const create = useCreateSubscriptionPlan();
  const toggle = useToggleSubscriptionPlan();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stripePriceId, setStripePriceId] = useState('');
  const [monthlyDollars, setMonthlyDollars] = useState<number>(150);

  async function submit() {
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        stripe_price_id: stripePriceId.trim(),
        monthly_cents: Math.round(monthlyDollars * 100),
        active: true,
      });
      setName('');
      setDescription('');
      setStripePriceId('');
      setMonthlyDollars(150);
      setShowForm(false);
      toast({ title: 'Plan added', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't add plan",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function onToggle(id: string, active: boolean) {
    try {
      await toggle.mutateAsync({ id, active });
    } catch (err) {
      toast({
        title: "Couldn't update plan",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing · Admin
        </p>
        <h1 className="mt-2">Subscription plans</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Plans map a studio tuition tier to a Stripe Price id. Create the Price in Stripe first,
          then enter the <code>price_xxx</code> id here.
        </p>
      </header>

      <div className="flex justify-end">
        <Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Add plan'}</Button>
      </div>

      {showForm && (
        <section
          aria-labelledby="new-plan-heading"
          className="surface-elevated grid gap-4 p-5 sm:grid-cols-2"
        >
          <h2 id="new-plan-heading" className="sr-only">
            New plan
          </h2>
          <div>
            <Label htmlFor="plan-name">Name</Label>
            <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="plan-price">Stripe Price id</Label>
            <Input
              id="plan-price"
              placeholder="price_xxx"
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="plan-monthly">Monthly price (USD)</Label>
            <Input
              id="plan-monthly"
              type="number"
              step="0.01"
              min="0"
              value={monthlyDollars}
              onChange={(e) => setMonthlyDollars(Number.parseFloat(e.target.value) || 0)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="plan-desc">Description</Label>
            <Input
              id="plan-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              onClick={() => void submit()}
              disabled={
                !name.trim() || !stripePriceId.trim() || monthlyDollars <= 0 || create.isPending
              }
            >
              {create.isPending ? 'Saving…' : 'Save plan'}
            </Button>
          </div>
        </section>
      )}

      {plans.isLoading && <p aria-live="polite">Loading…</p>}

      {!plans.isLoading && (plans.data?.length ?? 0) === 0 && (
        <Empty
          title="No plans yet"
          description="Add at least one plan to enable parent auto-pay."
        />
      )}

      <ul className="grid gap-3">
        {(plans.data ?? []).map((p) => (
          <li key={p.id} className="surface flex items-center justify-between p-4">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">{p.name}</p>
                <Badge tone={p.active ? 'success' : 'neutral'}>
                  {p.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
                {formatCents(p.monthly_cents)} / mo · {p.stripe_price_id}
              </p>
              {p.description && (
                <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {p.description}
                </p>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={() => void onToggle(p.id, !p.active)}
              disabled={toggle.isPending}
            >
              {p.active ? 'Deactivate' : 'Activate'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
